import React, { useCallback, useState, useEffect, useRef } from 'react';
import {
  Container,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableHead,
  Typography,
  TextField,
  InputAdornment,
  LinearProgress,
} from '@mui/material';
import { useHistory } from 'react-router-dom';
import DissatisfiedIcon from '@mui/icons-material/SentimentVeryDissatisfied';
import { useClient } from 'shared/apollo';
import { useGraphQLRequest } from 'shared/auth/hooks/useApiRequest';
import SearchIcon from '@mui/icons-material/Search';
import MergeIcon from '@mui/icons-material/MergeType';
import MultiFilter from 'shared/account/pages/Notifications/MultiFilter';
import {
  useStyles,
  TableRoot,
  TableRow,
  Heading,
  RowH,
  ColH,
  SortDownIcon,
  SortUpIcon,
  StickyBox,
  ToolbarRow,
  CloseIcon,
  CancelButton,
  BoxCenter,
} from './styles';
import UsersModal from './UsersModal';
import NewUserAccountPicker from './NewUserAccountPicker';
import AccountEditModal from './AccountEditModal';
import DonorsModal from './DonorsModal';
import GranteesModal from './GranteesModal';
import MergeModal from './MergeModal';
import { getAccounts } from './api';
import { Account, MergeAccounts } from './types';
import AccountRow from './AccountRow';

const AccountManagement: React.FC = () => {
  const { push } = useHistory();
  const isFirstRender = useRef(true);
  const client = useClient();
  const classes = useStyles();
  const [search, setSearch] = useState<string>('');
  const [filterType, setFilterType] = useState<string>('Any');
  const [showAddUserModal, setShowAddUserModal] = useState<boolean>(false);
  const [showAccountEditModal, setShowAccountEditModal] = useState<boolean>(
    false,
  );
  const [showDonorsModal, setShowDonorsModal] = useState<boolean>(false);
  const [showGranteesModal, setShowGranteesModal] = useState<boolean>(false);
  const [accountList, setAccountList] = useState<Account[]>([]);
  const [offset, setOffset] = useState<number>(0);
  const [selectedAccount, setSelectedAccount] = useState<Account | undefined>(
    undefined,
  );
  const [mergeAccounts, setMergeAccounts] = useState<MergeAccounts>({
    main: undefined,
    secondary: undefined,
  });
  const [mergeSuccess, setMergeSuccess] = useState<boolean>(false);
  const [mergedAlreadyAccounts, setMergedAlreadyAccounts] = useState<
    MergeAccounts
  >({
    main: undefined,
    secondary: undefined,
  });
  const [showMergeModal, setShowMergeModal] = useState<boolean>(false);
  const [showNewUserPicker, setShowNewUserPicker] = useState<boolean>(false);

  const [requestState, makeRequest] = useGraphQLRequest<{
    adminAccounts: Account[];
  }>();

  const [sortBy, setSortBy] = useState<string>('NAME_ASC');

  const isLoading = requestState.type === 'REQUEST_START';

  const fetch = (overrideSearch?: string, overrideFilterType?: string) => {
    makeRequest(
      getAccounts(
        client!,
        20,
        offset,
        sortBy,
        overrideSearch !== undefined ? overrideSearch : search,
        overrideFilterType !== undefined ? overrideFilterType : filterType,
      ),
    );
  };

  useEffect(() => {
    fetch();
  }, [offset]);

  useEffect(() => {
    console.log('sortBy fatch');
    fetch();
  }, [sortBy]);

  useEffect(() => {
    if (requestState?.data?.adminAccounts) {
      setAccountList(prevData => [
        ...prevData,
        ...requestState?.data?.adminAccounts,
      ]);
    }
  }, [requestState?.data?.adminAccounts]);

  useEffect(() => {
    const handleScroll = () => {
      if (accountList && accountList.length === 0) return;
      const threshold = 0;
      const currentPosition =
        window.innerHeight + document.documentElement.scrollTop;
      const pageHeight = document.documentElement.scrollHeight;
      if (pageHeight - currentPosition <= threshold) {
        setOffset(prevOffset => prevOffset + 20);
      }
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [accountList]);

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    if (offset === 0 && filterType && accountList.length === 0) {
      fetch();
    }
  }, [filterType]);

  const handleMerge = (account: Account) => {
    if (!mergeAccounts.main) {
      setMergeAccounts({ ...mergeAccounts, main: account });
      setShowMergeModal(true);
    } else {
      // presed again the same main
      if (account.dbId === mergeAccounts?.main?.dbId) {
        setMergeAccounts({
          ...mergeAccounts,
          main: undefined,
          secondary: undefined,
        });
        return;
      }
      // presed again the same seconday
      if (account.dbId === mergeAccounts?.secondary?.dbId) {
        setMergeAccounts({ ...mergeAccounts, secondary: undefined });
        return;
      }
      setMergeAccounts({ ...mergeAccounts, secondary: account });
      if (mergeAccounts.main) {
        setShowMergeModal(true);
      }
    }
  };

  const handleCustomQuestionClick = useCallback(
    (account: Account) => {
      push(`/account/manage-accounts/custom-questions/${account.dbId}`);
    },
    [push],
  );

  const handleCancelMerge = () => {
    setMergeAccounts({ main: undefined, secondary: undefined });
    setMergeSuccess(false);
  };

  const handleSearch = () => {
    setAccountList([]);
    setOffset(0);
    fetch();
  };

  const handlePressSort = (field: string) => {
    const currentField = sortBy.split('_')[0];
    const sortOrder = sortBy.endsWith('ASC') ? 'DESC' : 'ASC';
    let newSortBy = `${field}_${sortOrder}`;
    if (field !== currentField) {
      newSortBy = `${field}_DESC`;
    }
    setAccountList([]);
    setOffset(0);
    setSortBy(newSortBy);
  };

  return (
    <Container maxWidth={false} className={classes.container}>
      <Grid
        container
        spacing={2}
        justify="center"
        alignItems="center"
        className={classes.root}
      >
        <Heading>
          <RowH>
            <ColH>
              <Typography variant="h2">Accounts Management</Typography>
            </ColH>
          </RowH>
          <ToolbarRow>
            <div className="toolbar-field toolbar-filter">
              <label htmlFor="accounts-filter">Filter By:</label>
              <MultiFilter
                options={['Any', 'funder', 'grantee']}
                value={filterType}
                placeholder="Type"
                multiple={false}
                onChange={async value => {
                  setAccountList([]);
                  setOffset(0);
                  setFilterType(value);
                }}
              />
            </div>
            <div className="toolbar-field toolbar-search">
              <label htmlFor="accounts-search">Search:</label>
              <TextField
                id="accounts-search"
                placeholder="Search by name, EIN or email"
                value={search}
                fullWidth
                onChange={e => setSearch(e.target.value)}
                onKeyDown={e => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleSearch();
                  }
                }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
              />
            </div>
            <div className="toolbar-field toolbar-action">
              <CancelButton onClick={() => setShowNewUserPicker(true)}>
                Add new user
              </CancelButton>
            </div>
            <div className="toolbar-spacer" />
          </ToolbarRow>
          <RowH>
            {mergeSuccess || mergeAccounts?.main ? (
              <StickyBox>
                <CloseIcon onClick={handleCancelMerge} />
                {mergeSuccess && (
                  <>
                    <span>Success!!</span>
                    <Typography variant="body1">
                      Accounts were successfully merged!
                    </Typography>
                    <Typography variant="body1">
                      Main account:{' '}
                      <b>{mergedAlreadyAccounts?.main?.name || '-'}</b> (ID:{' '}
                      <b>{mergedAlreadyAccounts?.main?.dbId}</b>)
                    </Typography>
                    <Typography variant="body1">
                      Secondary account:{' '}
                      <b>{mergedAlreadyAccounts?.secondary?.name || '-'}</b>{' '}
                      (ID: <b>{mergedAlreadyAccounts?.secondary?.dbId}</b>)
                    </Typography>
                    <br />
                  </>
                )}
                {!mergeSuccess && mergeAccounts?.main && (
                  <>
                    <Typography
                      variant="h6"
                      style={{ fontWeight: 600, marginBottom: '16px' }}
                    >
                      Merge Accounts
                    </Typography>
                    <div
                      style={{
                        display: 'grid',
                        gridTemplateColumns: 'auto 1fr',
                        gap: '4px 12px',
                        alignItems: 'start',
                      }}
                    >
                      <Typography variant="body1">
                        <b>Main:</b>
                      </Typography>
                      <Typography variant="body1">
                        {mergeAccounts?.main?.name || '-'} (ID:{' '}
                        {mergeAccounts?.main?.dbId})
                      </Typography>
                      <div></div>
                      <Typography variant="body1" style={{ color: '#666' }}>
                        {mergeAccounts?.main?.users?.[0]?.email || '-'}
                      </Typography>
                      <Typography
                        variant="body1"
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          marginTop: '8px',
                        }}
                      >
                        <b>Secondary:</b>
                      </Typography>
                      <Typography
                        variant="body1"
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          marginTop: '8px',
                        }}
                      >
                        {!mergeAccounts?.secondary && (
                          <>
                            <span style={{ color: '#888' }}>
                              Select from list below
                            </span>
                            <MergeIcon
                              style={{ marginLeft: '8px', fontSize: '20px' }}
                              color="disabled"
                            />
                          </>
                        )}
                        {mergeAccounts?.secondary && (
                          <>
                            {mergeAccounts?.secondary?.name || '-'} (ID:{' '}
                            {mergeAccounts?.secondary?.dbId})
                          </>
                        )}
                      </Typography>
                      {mergeAccounts?.secondary && (
                        <>
                          <div></div>
                          <Typography variant="body1" style={{ color: '#666' }}>
                            {mergeAccounts?.secondary?.users?.[0]?.email || '-'}
                          </Typography>
                        </>
                      )}
                    </div>
                  </>
                )}
              </StickyBox>
            ) : null}
          </RowH>
        </Heading>
        <TableRoot>
          <Table aria-label="accounts table">
            <TableHead>
              <TableRow>
                <TableCell align="left" onClick={() => handlePressSort('ID')}>
                  ID {sortBy === 'ID_DESC' ? <SortUpIcon /> : <SortDownIcon />}
                </TableCell>
                <TableCell align="left" onClick={() => handlePressSort('NAME')}>
                  Name{' '}
                  {sortBy === 'NAME_DESC' ? <SortUpIcon /> : <SortDownIcon />}
                </TableCell>
                <TableCell align="left" onClick={() => handlePressSort('TYPE')}>
                  Type{' '}
                  {sortBy === 'TYPE_DESC' ? <SortUpIcon /> : <SortDownIcon />}
                </TableCell>
                <TableCell
                  align="left"
                  onClick={() => handlePressSort('LOCATION')}
                >
                  Location{' '}
                  {sortBy === 'LOCATION_DESC' ? (
                    <SortUpIcon />
                  ) : (
                    <SortDownIcon />
                  )}
                </TableCell>
                <TableCell align="left">View Grantees</TableCell>
                <TableCell align="left">View Donors</TableCell>
                <TableCell align="left">Users</TableCell>
                <TableCell align="left">Custom Questions</TableCell>
                <TableCell align="center">Edit</TableCell>
                <TableCell align="center">Merge</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {accountList.map(account => (
                <AccountRow
                  key={account.dbId}
                  row={account}
                  onPressUsers={() => {
                    setSelectedAccount(account);
                    setShowAddUserModal(true);
                  }}
                  onPressEdit={() => {
                    setSelectedAccount(account);
                    setShowAccountEditModal(true);
                  }}
                  onPressDonors={() => {
                    if (account.funders.length === 0) return;
                    setSelectedAccount(account);
                    setShowDonorsModal(true);
                  }}
                  onPressGrantees={() => {
                    if (account.grantees.length === 0) return;
                    setSelectedAccount(account);
                    setShowGranteesModal(true);
                  }}
                  onPressMerge={handleMerge}
                  onPressCustomQuestions={handleCustomQuestionClick}
                  mergeAccounts={mergeAccounts}
                />
              ))}
            </TableBody>
          </Table>
          {isLoading && (
            <Grid item xs={12}>
              <LinearProgress variant="query" />
            </Grid>
          )}
          {accountList.length === 0 && !isLoading && (
            <BoxCenter>
              <DissatisfiedIcon style={{ fontSize: 40 }} />
              <Typography variant="body1">
                Your search didn't return any results.
              </Typography>
            </BoxCenter>
          )}
        </TableRoot>
      </Grid>
      {selectedAccount && (
        <UsersModal
          open={showAddUserModal}
          setOpen={setShowAddUserModal}
          handleClose={response => {
            setShowAddUserModal(false);
            setSelectedAccount();
            if (response?.success) {
              setAccountList([]);
              if (offset > 0) {
                setOffset(0);
              } else {
                fetch();
              }
            }
          }}
          account={selectedAccount}
        />
      )}
      {showNewUserPicker && (
        <NewUserAccountPicker
          open={showNewUserPicker}
          handleClose={response => {
            setShowNewUserPicker(false);
            if (response?.success) {
              setAccountList([]);
              if (offset > 0) {
                setOffset(0);
              } else {
                fetch();
              }
            }
          }}
        />
      )}
      {selectedAccount && (
        <AccountEditModal
          open={showAccountEditModal}
          setOpen={setShowAccountEditModal}
          account={selectedAccount}
          handleClose={response => {
            setShowAccountEditModal(false);
            setSelectedAccount();
            if (response?.success) {
              setAccountList([]);
              if (offset > 0) {
                setOffset(0);
              } else {
                fetch();
              }
            }
          }}
        />
      )}
      {selectedAccount && (
        <DonorsModal
          open={showDonorsModal}
          setOpen={setShowDonorsModal}
          account={selectedAccount}
          handleClose={() => {
            setShowDonorsModal(false);
            setSelectedAccount();
          }}
        />
      )}
      {selectedAccount && (
        <GranteesModal
          open={showGranteesModal}
          setOpen={setShowGranteesModal}
          account={selectedAccount}
          handleClose={() => {
            setShowGranteesModal(false);
            setSelectedAccount();
          }}
        />
      )}
      {mergeAccounts?.main && (
        <MergeModal
          open={showMergeModal}
          setOpen={setShowMergeModal}
          handleCancel={() => {
            setShowMergeModal(false);
            handleCancelMerge();
          }}
          handleClose={() => {
            setShowMergeModal(false);
            setSearch('');
            setFilterType('grantee');
            setAccountList([]);
            setOffset(0);
            fetch('', 'grantee');
          }}
          handleCompleted={() => {
            setShowMergeModal(false);
            setAccountList([]);
            setMergeSuccess(true);
            setMergedAlreadyAccounts({ ...mergeAccounts });
            setMergeAccounts({ main: undefined, secondary: undefined });
            if (offset > 0) {
              setOffset(0);
            } else {
              fetch();
            }
          }}
          main={mergeAccounts?.main}
          secondary={mergeAccounts?.secondary}
        />
      )}
    </Container>
  );
};

export default AccountManagement;
